import React, { useState, useCallback, useRef, useEffect } from "react";
import DataTable from "react-data-table-component";
import { Spinner, Button, Form, Card, Row, Col, Badge, Dropdown, Accordion } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import { FiRefreshCw, FiSearch, FiX, FiChevronDown, FiChevronUp, FiEdit, FiEye, FiPlus, FiTrash2 } from "react-icons/fi";
import { FaCheckCircle, FaTimesCircle, FaExchangeAlt } from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import moment from "moment-timezone";
import TooltipCell from "../Shared/Tootip"
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ApplicationViewModal from "../Shared/ApplicationViewModal";
import jsPDF from "jspdf";
import { PDFDocument } from 'pdf-lib';

const InputField = ({ label, value, onChange, type = "text", placeholder = "", size = "sm", required = false }) => (
    <Form.Group className="mb-3">
        <Form.Label style={{ fontSize: "14px" }}>
            {label}
            {required && <span className="text-danger"> *</span>}
        </Form.Label>
        <Form.Control
            type={type}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            style={{ fontSize: "14px" }}
            size={size}
            required={required}
        />
    </Form.Group>
);

const TextareaField = ({ label, value, onChange, rows = 2, required = false }) => (
    <Form.Group className="mb-3">
        <Form.Label style={{ fontSize: "14px" }}>
            {label}
            {required && <span className="text-danger"> *</span>}
        </Form.Label>
        <Form.Control as="textarea" rows={rows} value={value} onChange={onChange} style={{ fontSize: "14px" }} required={required} />
    </Form.Group>
);

const DropdownField = ({ label, options, selected, onSelect, open, setOpen }) => (
    <Form.Group className="mb-3">
        <Form.Label style={{ fontSize: "14px" }}>{label}</Form.Label>
        <Dropdown onToggle={(isOpen) => setOpen(isOpen)}>
            <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-flex align-items-center justify-content-between w-100" style={{ fontSize: "14px" }}>
                {selected}
                {open ? <FiChevronUp /> : <FiChevronDown />}
            </Dropdown.Toggle>
            <Dropdown.Menu style={{ minWidth: "100%" }}>
                {options.map((opt) => (
                    <Dropdown.Item key={opt} active={selected === opt} onClick={() => onSelect(opt)} className="w-100">
                        {opt}
                    </Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    </Form.Group>
);

const MultipleFileUploadField = ({ label, value, onChange, existingFileUrl = null, required = false, accept = "image/*,.pdf,.doc,.docx,.txt,.text,.rtf,.md,.csv" }) => {
    const [files, setFiles] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [mergedPdfPreview, setMergedPdfPreview] = useState(null);
    const [showMergeButton, setShowMergeButton] = useState(false);
    const [isMerging, setIsMerging] = useState(false);
    const inputRef = useRef(null);

    // Fix: Proper state initialization with existing files
    useEffect(() => {
        if (existingFileUrl && files.length === 0) {
            setFiles([{
                name: "Existing File",
                url: existingFileUrl,
                isExisting: true,
                preview: existingFileUrl
            }]);
        }
    }, [existingFileUrl]);

    // Fix: Also track when the component receives new value prop (for editing)
    useEffect(() => {
        if (value && value instanceof File && !files.some(f => !f.isExisting)) {
            const newFile = {
                file: value,
                name: value.name,
                type: value.type,
                preview: URL.createObjectURL(value),
                isExisting: false
            };
            setFiles([newFile]);
        }
    }, [value]);

    const getFileTypeFromName = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();
        const typeMap = {
            'pdf': 'application/pdf',
            'jpg': 'image/jpeg',
            'jpeg': 'image/jpeg',
            'png': 'image/png',
            'gif': 'image/gif',
            'doc': 'application/msword',
            'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'txt': 'text/plain',
            'text': 'text/plain',
            'rtf': 'application/rtf',
            'md': 'text/markdown',
            'csv': 'text/csv'
        };
        return typeMap[extension] || 'application/octet-stream';
    };

    const readFileAsArrayBuffer = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsArrayBuffer(file);
        });
    };

    const handleFileAdd = async (e) => {
        if (!e.target.files || e.target.files.length === 0) return;

        setIsProcessing(true);
        try {
            const newFiles = Array.from(e.target.files).map(file => ({
                file,
                name: file.name,
                type: file.type || getFileTypeFromName(file.name),
                preview: URL.createObjectURL(file),
                isExisting: false
            }));

            const updatedFiles = [...files, ...newFiles];
            setFiles(updatedFiles);

            // Check if we should show merge button for PDFs
            const nonExistingFiles = updatedFiles.filter(file => !file.isExisting);
            const pdfFiles = nonExistingFiles.filter(file =>
                file.type === 'application/pdf' || getFileTypeFromName(file.name) === 'application/pdf'
            );

            // Show merge button only if we have multiple PDF files
            setShowMergeButton(pdfFiles.length > 1);

            // Handle file processing based on file types and count
            if (!showMergeButton) {
                await processFiles(updatedFiles);
            }
            // For multiple PDFs, wait for merge button click

        } catch (error) {
            console.error('Error adding files:', error);
            toast.error('Failed to process files');
        } finally {
            setIsProcessing(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    // Separate file processing logic (for images and single files)
    const processFiles = async (fileList) => {
        if (fileList.length === 0) {
            onChange(null);
            setMergedPdfPreview(null);
            return;
        }

        // Get only non-existing files for processing
        const nonExistingFiles = fileList.filter(file => !file.isExisting);

        if (nonExistingFiles.length === 0) {
            // Only existing files, no processing needed
            return;
        }

        // Check if we have only images (for merging) or mixed files
        const imageFiles = nonExistingFiles.filter(file =>
            file.type?.startsWith('image/') || getFileTypeFromName(file.name)?.startsWith('image/')
        );
        const pdfFiles = nonExistingFiles.filter(file =>
            file.type === 'application/pdf' || getFileTypeFromName(file.name) === 'application/pdf'
        );
        const otherFiles = nonExistingFiles.filter(file =>
            !file.type?.startsWith('image/') &&
            !(file.type === 'application/pdf' || getFileTypeFromName(file.name) === 'application/pdf')
        );

        // Logic for different scenarios
        if (nonExistingFiles.length === 1) {
            // Single file - pass through directly
            onChange(nonExistingFiles[0].file);
            setMergedPdfPreview(null);
        } else if (imageFiles.length > 0 && imageFiles.length === nonExistingFiles.length) {
            // Multiple images only - merge into PDF
            await mergeImagesToPDF(fileList);
        } else if (pdfFiles.length === 1 && pdfFiles.length === nonExistingFiles.length) {
            // Single PDF - pass through directly
            onChange(pdfFiles[0].file);
            setMergedPdfPreview(null);
        } else {
            // Mixed files or multiple PDFs (wait for merge button)
            // Don't auto-process, wait for user action
            onChange(null);
            setMergedPdfPreview(null);
        }
    };

    // Function for merging images to PDF (from your original code)
    const mergeImagesToPDF = async (fileList) => {
        setIsProcessing(true);
        try {
            const mergedPdf = await PDFDocument.create();
            const imageFiles = fileList.filter(file =>
                !file.isExisting && (file.type?.startsWith('image/') || getFileTypeFromName(file.name)?.startsWith('image/'))
            );

            for (const imageFile of imageFiles) {
                await convertImageToPdfPage(mergedPdf, imageFile);
            }

            // Add existing files information if any
            const existingFiles = fileList.filter(file => file.isExisting);
            if (existingFiles.length > 0) {
                await addExistingFilesInfo(mergedPdf, existingFiles);
            }

            const mergedPdfBytes = await mergedPdf.save();
            const finalPdfBlob = new Blob([mergedPdfBytes], { type: 'application/pdf' });

            // Create preview URL for merged PDF
            const mergedPdfPreviewUrl = URL.createObjectURL(finalPdfBlob);
            setMergedPdfPreview(mergedPdfPreviewUrl);

            const pdfFile = new File(
                [finalPdfBlob],
                `${label.replace(/\s+/g, '_')}_images_${Date.now()}.pdf`,
                { type: 'application/pdf' }
            );

            onChange(pdfFile);
        } catch (error) {
            console.error('Image PDF merging error:', error);
            toast.error('Failed to merge images to PDF');
        } finally {
            setIsProcessing(false);
        }
    };

    // New function to handle backend PDF merging for multiple PDFs
    const handleMergePdfs = async () => {
        const nonExistingFiles = files.filter(file => !file.isExisting);
        const pdfFiles = nonExistingFiles.filter(file =>
            file.type === 'application/pdf' || getFileTypeFromName(file.name) === 'application/pdf'
        );

        if (pdfFiles.length < 2) {
            toast.warning('Need at least 2 PDF files to merge');
            return;
        }

        setIsMerging(true);
        try {
            const formData = new FormData();

            // Add all PDF files to FormData
            pdfFiles.forEach((fileObj, index) => {
                if (fileObj.file instanceof File) {
                    formData.append('pdfs', fileObj.file);
                }
            });

            // Add metadata
            formData.append('label', label);
            formData.append('timestamp', Date.now().toString());

            // Send to backend for merging
            const response = await axios.post(
                'https://www.azzenterprises.com/api/aws/merge-pdfs',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    timeout: 30000
                }
            );

            if (response.data && response.data.mergedUrl) {
                // Set the merged PDF URL
                setMergedPdfPreview(response.data.mergedUrl);

                // Create a file object from the merged URL
                const mergedFile = await urlToFile(response.data.mergedUrl, `merged_${label}_${Date.now()}.pdf`);
                onChange(mergedFile);

                toast.success('PDFs merged successfully!');
            } else {
                throw new Error('No merged URL returned from server');
            }

        } catch (error) {
            console.error('PDF merging error:', error);
            // Fallback to client-side merging if backend fails
            await mergePDFFilesClientSide(pdfFiles);
        } finally {
            setIsMerging(false);
        }
    };

    // Fallback client-side PDF merging
    const mergePDFFilesClientSide = async (pdfFiles) => {
        try {
            const mergedPdf = await PDFDocument.create();

            for (const pdfFile of pdfFiles) {
                try {
                    const pdfBytes = await readFileAsArrayBuffer(pdfFile.file);
                    const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
                    const pageIndices = pdfDoc.getPageIndices();
                    const copiedPages = await mergedPdf.copyPages(pdfDoc, pageIndices);
                    copiedPages.forEach((page) => {
                        mergedPdf.addPage(page);
                    });
                } catch (error) {
                    console.error(`Error processing PDF ${pdfFile.name}:`, error);
                    // Add error page
                    const errorPage = mergedPdf.addPage();
                    errorPage.drawText(`Error: ${pdfFile.name}`, {
                        x: 50, y: 400, size: 12,
                    });
                }
            }

            const mergedPdfBytes = await mergedPdf.save();
            const finalPdfBlob = new Blob([mergedPdfBytes], { type: 'application/pdf' });

            // Create preview URL for merged PDF
            const mergedPdfPreviewUrl = URL.createObjectURL(finalPdfBlob);
            setMergedPdfPreview(mergedPdfPreviewUrl);

            const pdfFile = new File(
                [finalPdfBlob],
                `${label.replace(/\s+/g, '_')}_merged_${Date.now()}.pdf`,
                { type: 'application/pdf' }
            );

            onChange(pdfFile);
            toast.success('PDFs merged successfully!');
        } catch (error) {
            console.error('Client-side PDF merging error:', error);
            toast.error('Failed to merge PDF files. Please try again.');
        }
    };

    // Helper function to convert URL to File object
    const urlToFile = async (url, filename) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            return new File([blob], filename, { type: 'application/pdf' });
        } catch (error) {
            console.error('Error converting URL to file:', error);
            // Fallback: create a placeholder file
            return new File([new Blob()], filename, { type: 'application/pdf' });
        }
    };

    // Image to PDF conversion functions (from your original code)
    const convertImageToPdfPage = async (pdfDoc, imageFile) => {
        try {
            let imageBytes;
            if (imageFile.file instanceof File) {
                imageBytes = await readFileAsArrayBuffer(imageFile.file);
            } else {
                return;
            }

            let image;
            const fileType = imageFile.type || getFileTypeFromName(imageFile.name);

            if (fileType === 'image/jpeg' || fileType === 'image/jpg') {
                image = await pdfDoc.embedJpg(imageBytes);
            } else if (fileType === 'image/png') {
                image = await pdfDoc.embedPng(imageBytes);
            } else {
                try {
                    image = await pdfDoc.embedJpg(imageBytes);
                } catch {
                    try {
                        image = await pdfDoc.embedPng(imageBytes);
                    } catch (error) {
                        console.error(`Cannot embed image ${imageFile.name}:`, error);
                        return;
                    }
                }
            }

            const page = pdfDoc.addPage();
            const { width, height } = page.getSize();
            const imageDims = image.scaleToFit(width - 40, height - 40);

            page.drawImage(image, {
                x: (width - imageDims.width) / 2,
                y: (height - imageDims.height) / 2,
                width: imageDims.width,
                height: imageDims.height,
            });

            page.drawText(`Image: ${imageFile.name}`, {
                x: 20, y: 20, size: 10,
            });

        } catch (error) {
            console.error(`Error converting image ${imageFile.name}:`, error);
            const page = pdfDoc.addPage();
            page.drawText(`Error processing image: ${imageFile.name}`, {
                x: 20, y: 400, size: 12,
            });
        }
    };

    const addExistingFilesInfo = async (pdfDoc, existingFiles) => {
        const existingFilesPage = pdfDoc.addPage();
        const { width, height } = existingFilesPage.getSize();
        existingFilesPage.drawText(`Previously Uploaded Files:`, {
            x: 50, y: height - 50, size: 16,
        });

        let yPos = height - 80;
        existingFiles.forEach((file, index) => {
            if (yPos < 100) {
                const continuedPage = pdfDoc.addPage();
                yPos = height - 50;
                continuedPage.drawText(`Previously Uploaded Files (Continued):`, {
                    x: 50, y: yPos, size: 16,
                });
                yPos -= 30;
            }

            existingFilesPage.drawText(`${index + 1}. ${file.name}`, {
                x: 70, y: yPos, size: 10,
            });

            const urlText = file.url && file.url.length > 60 ? file.url.substring(0, 60) + '...' : (file.url || 'No URL');
            existingFilesPage.drawText(`URL: ${urlText}`, {
                x: 90, y: yPos - 15, size: 8,
            });

            yPos -= 35;
        });
    };

    const handleRemoveFile = async (index) => {
        const fileToRemove = files[index];

        // Clean up preview URL if it's not an existing file
        if (!fileToRemove.isExisting && fileToRemove.preview) {
            URL.revokeObjectURL(fileToRemove.preview);
        }

        const updatedFiles = files.filter((_, i) => i !== index);
        setFiles(updatedFiles);

        // Update merge button visibility
        const nonExistingFiles = updatedFiles.filter(file => !file.isExisting);
        const pdfFiles = nonExistingFiles.filter(file =>
            file.type === 'application/pdf' || getFileTypeFromName(file.name) === 'application/pdf'
        );
        setShowMergeButton(pdfFiles.length > 1);

        if (updatedFiles.length === 0) {
            onChange(null);
            setMergedPdfPreview(null);
            setShowMergeButton(false);
        } else if (nonExistingFiles.length === 1) {
            // Single file remaining
            onChange(nonExistingFiles[0].file);
            setMergedPdfPreview(null);
        } else {
            // Multiple files remaining - reprocess
            await processFiles(updatedFiles);
        }
    };

    const handleRemoveAll = () => {
        // Clean up all preview URLs
        files.forEach(file => {
            if (!file.isExisting && file.preview) {
                URL.revokeObjectURL(file.preview);
            }
        });

        setFiles([]);
        onChange(null);
        setMergedPdfPreview(null);
        setShowMergeButton(false);
        if (inputRef.current) inputRef.current.value = "";
    };

    const getFileTypeForDisplay = (fileObj) => {
        if (fileObj.isExisting) return 'existing';
        const fileType = fileObj.type || getFileTypeFromName(fileObj.name);
        if (fileType === 'application/pdf') return 'pdf';
        if (fileType?.startsWith('image/')) return 'image';
        return 'other';
    };

    const getProcessingMessage = () => {
        const nonExistingFiles = files.filter(file => !file.isExisting);
        const imageFiles = nonExistingFiles.filter(file =>
            file.type?.startsWith('image/') || getFileTypeFromName(file.name)?.startsWith('image/')
        );
        const pdfFiles = nonExistingFiles.filter(file =>
            file.type === 'application/pdf' || getFileTypeFromName(file.name) === 'application/pdf'
        );

        if (nonExistingFiles.length === 0) {
            return "No new files selected";
        } else if (nonExistingFiles.length === 1) {
            return "Single file - will be uploaded as is";
        } else if (imageFiles.length === nonExistingFiles.length) {
            return "Multiple images - will be merged into single PDF";
        } else if (pdfFiles.length > 1) {
            return "Multiple PDFs - click Merge button to combine";
        } else {
            return "Multiple files - will be processed individually";
        }
    };

    // Clean up preview URLs when component unmounts
    useEffect(() => {
        return () => {
            files.forEach(file => {
                if (!file.isExisting && file.preview) {
                    URL.revokeObjectURL(file.preview);
                }
            });
            if (mergedPdfPreview) {
                URL.revokeObjectURL(mergedPdfPreview);
            }
        };
    }, [files, mergedPdfPreview]);

    return (
        <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: "14px" }}>
                {label}
                {required && <span className="text-danger"> *</span>}
            </Form.Label>

            <div className="d-flex align-items-center gap-2 mb-2">
                <Form.Control
                    type="file"
                    accept={accept}
                    onChange={handleFileAdd}
                    style={{ flex: 1 }}
                    ref={inputRef}
                    multiple
                    disabled={isProcessing || isMerging}
                />
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => inputRef.current?.click()}
                    style={{ fontSize: "12px", minWidth: "40px" }}
                    disabled={isProcessing || isMerging}
                >
                    <FiPlus size={12} />
                </Button>
                {files.length > 0 && (
                    <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={handleRemoveAll}
                        style={{ fontSize: "12px", minWidth: "40px" }}
                        disabled={isProcessing || isMerging}
                    >
                        <FiTrash2 size={12} />
                    </Button>
                )}
            </div>

            {/* Merge Button - Only show when multiple PDFs are present */}
            {showMergeButton && !mergedPdfPreview && (
                <div className="mb-2">
                    <Button
                        variant="warning"
                        size="sm"
                        onClick={handleMergePdfs}
                        disabled={isMerging}
                        style={{ fontSize: "12px" }}
                    >
                        {isMerging ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-1" />
                                Merging...
                            </>
                        ) : (
                            <>
                                <FaExchangeAlt className="me-1" />
                                Merge PDFs
                            </>
                        )}
                    </Button>
                    <small className="text-muted ms-2">
                        Click to merge {files.filter(f => !f.isExisting && (f.type === 'application/pdf' || getFileTypeFromName(f.name) === 'application/pdf')).length} PDF files
                    </small>
                </div>
            )}

            {(isProcessing || isMerging) && (
                <div className="d-flex align-items-center gap-2 mb-2">
                    <Spinner animation="border" size="sm" />
                    <small className="text-muted">
                        {isMerging ? "Merging PDFs..." : "Processing files..."}
                    </small>
                </div>
            )}

            {files.length > 0 && (
                <div className="mt-2">
                    <div className="d-flex align-items-center gap-1 mb-2">
                        <FaCheckCircle color="green" size={14} />
                        <span style={{ color: "green", fontWeight: "500", fontSize: "14px" }}>
                            {files.length} file(s) selected - {getProcessingMessage()}
                        </span>
                    </div>

                    {/* Show merged PDF preview when available */}
                    {mergedPdfPreview && (
                        <div className="mb-3 p-2 border rounded bg-light">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <FaCheckCircle color="blue" size={14} />
                                <span style={{ color: "blue", fontWeight: "500", fontSize: "14px" }}>
                                    Merged PDF Preview
                                </span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <a
                                    href={mergedPdfPreview}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-primary"
                                    style={{ fontSize: "12px" }}
                                >
                                    View Merged PDF
                                </a>
                                <span className="text-muted" style={{ fontSize: "12px" }}>
                                    All files have been merged into a single PDF
                                </span>
                            </div>
                        </div>
                    )}

                    <div className="file-list" style={{ maxHeight: "150px", overflowY: "auto" }}>
                        {files.map((fileObj, index) => {
                            const fileType = getFileTypeForDisplay(fileObj);
                            const isPdf = fileType === 'pdf';

                            return (
                                <div
                                    key={index}
                                    className="d-flex justify-content-between align-items-center p-2 border rounded mb-1"
                                    style={{ fontSize: "12px" }}
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="text-truncate" style={{ maxWidth: "200px" }}>
                                            {fileObj.name}
                                        </span>
                                        {fileObj.isExisting && (
                                            <Badge bg="secondary" style={{ fontSize: "10px" }}>
                                                Existing
                                            </Badge>
                                        )}
                                        {isPdf && (
                                            <Badge bg="info" style={{ fontSize: "10px" }}>
                                                PDF
                                            </Badge>
                                        )}
                                        {fileType === 'image' && (
                                            <Badge bg="success" style={{ fontSize: "10px" }}>
                                                Image
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center gap-1">
                                        {fileObj.isExisting && fileObj.url && (
                                            <a
                                                href={fileObj.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-sm btn-outline-primary"
                                                style={{ fontSize: "10px", padding: "2px 6px" }}
                                            >
                                                View
                                            </a>
                                        )}
                                        {!fileObj.isExisting && fileObj.preview && !mergedPdfPreview && (
                                            <a
                                                href={fileObj.preview}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-sm btn-outline-info"
                                                style={{ fontSize: "10px", padding: "2px 6px" }}
                                            >
                                                Preview
                                            </a>
                                        )}
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleRemoveFile(index)}
                                            style={{ fontSize: "10px", padding: "2px 6px", minWidth: "30px" }}
                                            disabled={isProcessing || isMerging}
                                        >
                                            <FiTrash2 size={10} />
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </Form.Group>
    );
};

const ProgressBar = ({ currentStep, steps }) => {
    const percent = ((currentStep + 1) / steps.length) * 100;
    return (
        <div className="mt-3">
            <div className="d-flex justify-content-between mb-1">
                <small style={{ fontSize: "14px" }}>{steps[currentStep].title}</small>
                <small style={{ fontSize: "14px" }}>{percent.toFixed(0)}%</small>
            </div>
            <div className="progress" style={{ height: "8px", borderRadius: "4px" }}>
                <div className="progress-bar bg-primary" role="progressbar" style={{ width: `${percent}%`, transition: "width 0.3s ease" }}></div>
            </div>
        </div>
    );
};

const Application = () => {
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredApplicants, setFilteredApplicants] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [formType, setFormType] = useState("Applicant");
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const fetchingRef = useRef(false);
    const [loanTypeOpen, setLoanTypeOpen] = useState(false);
    const [loanPurposeOpen, setLoanPurposeOpen] = useState(false);
    const [empTypeOpen, setEmpTypeOpen] = useState(false);
    const [busConstOpen, setBusConstOpen] = useState(false);
    const [shopNameOpen, setShopNameOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [applicants, setApplicants] = useState([]);
    const [view, setView] = useState("applicant");
    const [viewModalShow, setViewModalShow] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [fileStates, setFileStates] = useState({});
    const [userRole, setUserRole] = useState("");

    useEffect(() => {
        const role = localStorage.getItem("user_role") || "";
        setUserRole(role);
    }, []);

    const fetchCustomerDetails = async (idNumber) => {
        if (!idNumber || idNumber.length < 3) return;

        try {
            setLoading(true);
            const response = await axios.get(`https://www.azzenterprises.com/api/customers/search/applicant?q=${encodeURIComponent(idNumber)}`);

            if (response.data && response.data.success && response.data.data.length > 0) {
                const customer = response.data.data[0];

                setFormData(prev => ({
                    ...prev,
                    name: customer.name || prev.name,
                    mob_num: customer.mob_num || prev.mob_num,
                    email_id: customer.email_id || prev.email_id,
                    dob: customer.dob ? moment(customer.dob).format('YYYY-MM-DD') : prev.dob,
                    contact_no: customer.contact_no || prev.contact_no,
                    id_number: customer.id_number || prev.id_number,
                    loan_amt: customer.loan_amt || prev.loan_amt,
                    model: customer.model || prev.model,
                    tenor: customer.tenor || prev.tenor,
                }));

                toast.success(`Customer details loaded for ${customer.name || 'existing customer'}`);
            } else {
                toast.info('No existing customer found with this ID');
            }
        } catch (error) {
            console.error('Error fetching customer details:', error);
            if (error.response?.status !== 404) {
                toast.error('Failed to fetch customer details');
            }
        } finally {
            setLoading(false);
        }
    };

    const checkExistingApplicant = async (idNumber) => {
        if (!idNumber || idNumber.trim().length < 3) return false;

        try {
            const response = await axios.get(
                `https://www.azzenterprises.com/api/application/check-existing/${encodeURIComponent(idNumber.trim())}`
            );

            if (response.data.exists) {
                const isEditingCurrentRecord = isEditing && response.data.data.id === editingId;

                if (!isEditingCurrentRecord) {
                    // if (!isEditing) {
                    //     toast.warning(
                    //         <div>
                    //             <strong>Applicant Already Exists!</strong>
                    //             <br />
                    //             This ID number is already registered as an applicant.
                    //             <br />
                    //             <small>You can edit the existing record instead.</small>
                    //         </div>,
                    //         { autoClose: 5000 }
                    //     );
                    // }

                    setFormData(prev => ({
                        ...prev,
                        _isDuplicate: !isEditingCurrentRecord,
                        _existingApplicantId: response.data.data.id
                    }));

                    return !isEditingCurrentRecord;
                }
            }

            // Clear duplicate flag
            setFormData(prev => ({
                ...prev,
                _isDuplicate: false,
                _existingApplicantId: null
            }));

            return false;
        } catch (error) {
            console.error('Error checking existing applicant:', error);
            return false;
        }
    };

    const handleIdNumberChange = (e) => {
        const value = e.target.value;
        handleInputChange(e, "id_number");

        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        if (value && value.length >= 3) {
            const newTimeout = setTimeout(async () => {
                const isExistingApplicant = await checkExistingApplicant(value);

                if (isExistingApplicant) {
                    return;
                }

                await fetchCustomerDetails(value);
            }, 1000);

            setSearchTimeout(newTimeout);
        } else {
            setFormData(prev => ({
                ...prev,
                _isDuplicate: false,
                _existingApplicantId: null
            }));
        }
    };

    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    const filterApplicants = useCallback((applicants, searchTerm) => {
        if (!searchTerm.trim()) {
            return applicants;
        }

        const lowercasedSearch = searchTerm.toLowerCase();

        return applicants.filter(applicant => {
            const searchableFields = [
                'name', 'id_number', 'email_id', 'mob_num', 'fs_name',
                'nation', 'address', 'landmark', 'designation', 'prof_id',
                'off_city', 'model', 'imei_no', 'loan_type', 'loan_purpose',
                'shop_name', 'marital', 'gender', 'permit', 'emp_type',
                'bus_const', 'contact_no'
            ];

            return searchableFields.some(field => {
                const value = applicant[field];
                return value && value.toString().toLowerCase().includes(lowercasedSearch);
            });
        });
    }, []);

    useEffect(() => {
        const filtered = filterApplicants(applicants, searchTerm);
        setFilteredApplicants(filtered);
    }, [searchTerm, applicants, filterApplicants]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleClearSearch = () => {
        setSearchTerm("");
    };

    const handleView = (row) => {
        setSelectedApplication(row);
        setViewModalShow(true);
    };

    const [formData, setFormData] = useState({
        id_number: "", loan_type: "EMI On Mobile", loan_amt: "", tenor: "", model: "", imei_no: "", loan_purpose: "Mobile Loan", shop_name: "AZZ",
        name: "", photo_s3: "", fs_name: "", nation: "Indian", marital: "Single", dob: "", gender: "Male", permit: "WP Pass", address: "", landmark: "", mob_num: "", email_id: "",
        emp_type: "Salaried", prof_id: "", designation: "", bus_const: "Private Limited Company", total_exp: "", off_address: "", off_landmark: "", off_city: "Singapore",
        color_photos3: "", passport_s3: "", ic_s3: "", inc_slips3: "", bank_states3: "", aadhaar_s3: "", pan_s3: "", address_s3: "", contact_no: "", verified_s3: "",
    });

    const [existingFiles, setExistingFiles] = useState({});

    const loanTypeList = ["EMI On Mobile"];
    const loanPurposeList = ["Mobile Loan"];
    const empTypeList = ["Salaried", "Self-Employed"];
    const busConstList = ["Public Limited Company", "Private Limited Company"];
    const shopNameList = ["AZZ"];

    const fetchData = async () => {
        setLoading(true);
        const urlMap = {
            applicant: "https://www.azzenterprises.com/api/application/get/applicants",
            referrer: "https://www.azzenterprises.com/api/application/get/referrers",
        };

        try {
            const res = await axios.get(urlMap[view]);
            const formatted = res.data.data.map((item, index) => ({
                ...item,
                no: index + 1,
                created_at: moment(item.created_at)
                    .tz("Asia/Singapore")
                    .format("YYYY-MM-DD HH:mm:ss"),
            }));
            setApplicants(formatted);
        } catch (err) {
            console.error(`Error fetching ${view}:`, err);
            toast.error(`Failed to load ${view}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [view]);

    const handleInputChange = (e, field) => setFormData((prev) => ({ ...prev, [field]: e.target.value }));

    // Fix: Update file upload to maintain state
    const handleFileUpload = (fieldName, file) => {
        setFormData((prev) => ({
            ...prev,
            [fieldName]: file,
        }));

        // Also store in file states for persistence
        setFileStates(prev => ({
            ...prev,
            [fieldName]: file
        }));
    };

    const handleEdit = (row) => {
        setIsEditing(true);
        setEditingId(row.id);
        setFormType(view === "applicant" ? "Applicant" : "Guarantor");

        const mappedData = {
            id_number: row.id_number || "",
            loan_type: row.loan_type || "EMI On Mobile",
            loan_amt: row.loan_amt || "",
            tenor: row.tenor || "",
            model: row.model || "",
            imei_no: row.imei_no || "",
            loan_purpose: row.loan_purpose || "Mobile Loan",
            shop_name: row.shop_name || "AZZ",
            name: row.name || "",
            photo_s3: fileStates.photo_s3 || null,
            fs_name: row.fs_name || "",
            nation: row.nation || "Indian",
            marital: row.marital || "Single",
            dob: row.dob ? moment(row.dob).format('YYYY-MM-DD') : "",
            gender: row.gender || "Male",
            permit: row.permit || "WP Pass",
            address: row.address || "",
            landmark: row.landmark || "",
            mob_num: row.mob_num || "",
            email_id: row.email_id || "",
            emp_type: row.emp_type || "Salaried",
            prof_id: row.prof_id || "",
            designation: row.designation || "",
            bus_const: row.bus_const || "Private Limited Company",
            total_exp: row.total_exp || "",
            off_address: row.off_address || "",
            off_landmark: row.off_landmark || "",
            off_city: row.off_city || "Singapore",
            color_photos3: fileStates.color_photos3 || null,
            passport_s3: fileStates.passport_s3 || null,
            ic_s3: fileStates.ic_s3 || null,
            inc_slips3: fileStates.inc_slips3 || null,
            bank_states3: fileStates.bank_states3 || null,
            aadhaar_s3: fileStates.aadhaar_s3 || null,
            pan_s3: fileStates.pan_s3 || null,
            address_s3: row.address_s3 || "",
            contact_no: row.contact_no || "",
            verified_s3: fileStates.verified_s3 || null,
        };

        const fileFields = [
            'photo_s3', 'color_photos3', 'passport_s3', 'ic_s3',
            'inc_slips3', 'bank_states3', 'aadhaar_s3', 'pan_s3', 'verified_s3'
        ];

        const existingFileUrls = {};
        fileFields.forEach(field => {
            if (row[field]) {
                existingFileUrls[field] = row[field];
            }
        });

        setExistingFiles(existingFileUrls);
        setFormData(mappedData);
        setShowForm(true);
        setCurrentStep(0);
    };

    const handleNextStep = async () => {
        if (currentStep === 0) {
            if (!formData.id_number) {
                toast.error("Id number(passport) is mandatory.");
                return;
            }

            if (!isEditing && formType === "Applicant") {
                const isDuplicate = await checkExistingApplicant(formData.id_number);
                if (isDuplicate) {
                    toast.error(
                        <div>
                            <strong>Cannot Create Duplicate Applicant</strong>
                            <br />
                            This ID number is already registered.
                            <br />
                            Please use a different ID or edit the existing record.
                        </div>,
                        { autoClose: 6000 }
                    );
                    return;
                }
            }

            if (formType === "Applicant" && (!formData.shop_name)) {
                toast.error("Shop name is mandatory.");
                return;
            }
            if (formType === "Applicant" && (!formData.verified_s3 && !existingFiles.verified_s3)) {
                toast.error("Verified screenshot(from MOM/SGWorkpass) is mandatory.");
                return;
            }
        }
        if (currentStep === 3) {
            if (!formData.ic_s3 && !existingFiles.ic_s3) {
                toast.error("IC Copy is mandatory.");
                return;
            }
            if (!formData.passport_s3 && !existingFiles.passport_s3) {
                toast.error("ID Proof (Passport) is mandatory.");
                return;
            }
        }
        if (currentStep < 5) {
            await new Promise((resolve) => setTimeout(resolve, 300));
            setCurrentStep((prev) => prev + 1);
            return;
        }

        // const finalDuplicateCheck = await checkExistingApplicant(formData.id_number);
        // if (finalDuplicateCheck) {
        //     toast.error("This applicant already exists. Please use a different ID number.");
        //     return;
        // }

        setIsSubmitting(true);
        try {
            const filesToUpload = [
                { field: "photo_s3", value: formData.photo_s3 },
                { field: "color_photos3", value: formData.color_photos3 },
                { field: "passport_s3", value: formData.passport_s3 },
                { field: "ic_s3", value: formData.ic_s3 },
                { field: "inc_slips3", value: formData.inc_slips3 },
                { field: "bank_states3", value: formData.bank_states3 },
                { field: "aadhaar_s3", value: formData.aadhaar_s3 },
                { field: "pan_s3", value: formData.pan_s3 },
                { field: "verified_s3", value: formData.verified_s3 },
            ];

            const formDataToSubmit = { ...formData };

            Object.keys(existingFiles).forEach(field => {
                if (!formData[field]) {
                    formDataToSubmit[field] = existingFiles[field];
                }
            });

            for (let fileObj of filesToUpload) {
                if (fileObj.value && fileObj.value instanceof File) {
                    const fd = new FormData();
                    fd.append("file", fileObj.value);
                    const response = await axios.post("https://www.azzenterprises.com/api/aws/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    formDataToSubmit[fileObj.field] = response.data.url;
                }
            }

            await new Promise((resolve) => setTimeout(resolve, 2000));

            let response;
            if (isEditing) {
                const updateUrl = formType === "Applicant"
                    ? `https://www.azzenterprises.com/api/application/update/applicant/${editingId}`
                    : `https://www.azzenterprises.com/api/application/update/guarantor/${editingId}`;

                response = await axios.put(updateUrl, formDataToSubmit);
            } else {
                if (formType === "Applicant") {
                    response = await axios.post(
                        "https://www.azzenterprises.com/api/application/submit/applicant",
                        formDataToSubmit
                    );
                } else if (formType === "Guarantor") {
                    response = await axios.post(
                        "https://www.azzenterprises.com/api/application/submit/guarantor",
                        formDataToSubmit
                    );
                }
            }

            if (response.data?.message) {
                toast.success(response.data.message);
            } else {
                toast.success(isEditing ? "Application updated successfully!" : "Application submitted successfully!");
            }
            console.log("Server response:", response.data);
            handleCancel();
            fetchData();
        } catch (error) {
            console.error("Submission failed:", error);
            toast.error(isEditing ? "Application update failed." : "Application submission failed.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePreviousStep = () => { if (currentStep > 0) setCurrentStep((prev) => prev - 1); };

    // Fix: Clear file states on cancel
    const handleCancel = () => {
        setShowForm(false);
        setCurrentStep(0);
        setFormType("Applicant");
        setIsEditing(false);
        setEditingId(null);
        setExistingFiles({});
        setFileStates({});
        setFormData({
            id_number: "", loan_type: "EMI On Mobile", loan_amt: "", tenor: "", model: "", imei_no: "", loan_purpose: "Mobile Loan", shop_name: "AZZ",
            name: "", photo_s3: "", fs_name: "", nation: "Indian", marital: "Single", dob: "", gender: "Male", permit: "WP Pass", address: "", landmark: "", mob_num: "", email_id: "",
            emp_type: "Salaried", prof_id: "", designation: "", bus_const: "Private Limited Company", total_exp: "", off_address: "", off_landmark: "", off_city: "",
            color_photos3: "", passport_s3: "", ic_s3: "", inc_slips3: "", bank_states3: "", aadhaar_s3: "", pan_s3: "", address_s3: "", contact_no: "", verified_s3: "",
        });
    };

    const steps = [
        { title: "Loan Details" },
        { title: formType === "Applicant" ? "Applicant Details" : "Referrer Details" },
        { title: "Employment Details" },
        { title: "Documents" },
        { title: "Other Details" },
        { title: "Review & Submit" },
    ];

    const FreshDownloadButton = ({ fileUrl }) => {
        const [isLoading, setIsLoading] = useState(false);

        const handleDownload = async (e) => {
            e?.preventDefault();
            if (!fileUrl || isLoading) return;

            setIsLoading(true);

            try {
                const cacheKey = `fresh_url_${btoa(fileUrl)}`;
                const cached = localStorage.getItem(cacheKey);

                if (cached) {
                    const { freshUrl, expiresAt } = JSON.parse(cached);

                    if (expiresAt && new Date(expiresAt) > new Date()) {
                        toast.success('Opening download...', { autoClose: 800 });

                        setTimeout(() => {
                            window.open(freshUrl, '_blank', 'noopener,noreferrer');
                            setIsLoading(false);
                        }, 1500);

                        return;
                    }
                }

                const response = await axios.post(
                    'https://www.azzenterprises.com/api/aws/regenerate-s3-url',
                    { storedUrl: fileUrl },
                    { timeout: 8000 }
                );

                if (!response.data?.success || !response.data?.freshUrl) {
                    throw new Error('Failed to get fresh URL');
                }

                toast.success('Opening download...', { autoClose: 800 });

                const cacheData = {
                    freshUrl: response.data.freshUrl,
                    expiresAt: response.data.expiresAt,
                    cachedAt: new Date().toISOString()
                };

                localStorage.setItem(cacheKey, JSON.stringify(cacheData));

                setTimeout(() => {
                    window.open(response.data.freshUrl, '_blank', 'noopener,noreferrer');
                    setIsLoading(false);
                }, 1500);

            } catch (error) {
                console.error('Download error:', error);

                toast.error('Download failed', { autoClose: 1500 });
                setIsLoading(false);
            }
        };

        if (!fileUrl) {
            return <span>—</span>;
        }

        return (
            <button
                onClick={handleDownload}
                style={{
                    fontSize: '0.8rem',
                    color: isLoading ? '#666' : '#0d6efd',
                    cursor: isLoading ? 'default' : 'pointer',
                    textDecoration: 'none',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit'
                }}
                disabled={isLoading}
            >
                {isLoading ? 'Preparing...' : 'Download'}
            </button>
        );
    };

    const columns = [
        { name: "#", selector: (row) => row.no, width: "60px" },
        { name: <span title="ID Number">ID Number</span>, cell: (row) => <TooltipCell text={row.id_number} />, width: "110px" },

        ...(view === "applicant"
            ? [
                { name: <span title="Loan Type">Loan Type</span>, cell: (row) => <TooltipCell text={row.loan_type} /> },
                { name: <span title="Loan Amount">Loan Amt</span>, cell: (row) => <TooltipCell text={row.loan_amt} />, width: "80px" },
                { name: <span title="Tenor">Tenor</span>, cell: (row) => <TooltipCell text={row.tenor} />, width: "60px" },
                { name: <span title="Model">Model</span>, cell: (row) => <TooltipCell text={row.model} /> },
                { name: <span title="IMEI No">IMEI No</span>, cell: (row) => <TooltipCell text={row.imei_no} /> },
                { name: <span title="Loan Purpose">Loan Purpose</span>, cell: (row) => <TooltipCell text={row.loan_purpose} /> },
                { name: <span title="Shop Name">Shop</span>, cell: (row) => <TooltipCell text={row.shop_name} />, width: "70px" },
            ]
            : []),
        { name: <span title="Applicant Name">Applicant Name</span>, selector: (row) => row.name || "—" },
        {
            name: <span title="Photo">Photograph</span>,
            cell: (row) =>
                row.photo_s3 ? (
                    <FreshDownloadButton fileUrl={row.photo_s3} />
                ) : (
                    "—"
                ),
        },
        { name: <span title="Father's Name">Father's Name</span>, cell: (row) => <TooltipCell text={row.fs_name} /> },
        { name: <span title="Nationality">Nationality</span>, cell: (row) => <TooltipCell text={row.nation} />, width: "90px" },
        { name: <span title="Marital Status">Marital</span>, cell: (row) => <TooltipCell text={row.marital} />, width: "90px" },
        {
            name: <span title="DOB">DOB</span>,
            cell: (row) => (
                <TooltipCell
                    text={row.dob ? moment(row.dob).tz("Asia/Kolkata").format("YYYY-MM-DD") : "—"}
                />
            ),
            width: "110px"
        },
        { name: <span title="Gender">Gender</span>, cell: (row) => <TooltipCell text={row.gender} />, width: "70px" },
        { name: <span title="Permit">Permit</span>, cell: (row) => <TooltipCell text={row.permit} />, width: "90px" },
        { name: <span title="Address">Address</span>, cell: (row) => <TooltipCell text={row.address} /> },
        { name: <span title="Landmark">Landmark</span>, cell: (row) => <TooltipCell text={row.landmark} /> },
        { name: <span title="Mobile">Mobile</span>, cell: (row) => <TooltipCell text={row.mob_num} /> },
        { name: <span title="Email">Email</span>, cell: (row) => <TooltipCell text={row.email_id} /> },

        { name: <span title="Employment Type">Emp Type</span>, cell: (row) => <TooltipCell text={row.emp_type} />, width: "90px" },
        { name: <span title="Profession ID">Profession ID</span>, cell: (row) => <TooltipCell text={row.prof_id} /> },
        { name: <span title="Designation">Designation</span>, cell: (row) => <TooltipCell text={row.designation} /> },
        { name: <span title="Business Const.">Business Const.</span>, cell: (row) => <TooltipCell text={row.bus_const} /> },
        { name: <span title="Experience">Experience</span>, cell: (row) => <TooltipCell text={row.total_exp} />, width: "90px" },
        {
            name: <span title="Office Address">Office Address</span>,
            cell: (row) => <TooltipCell text={row.off_address} />,
        },
        {
            name: <span title="Office Landmark">Office Landmark</span>,
            cell: (row) => <TooltipCell text={row.off_landmark} />,
        },
        { name: <span title="Office City">Office City</span>, cell: (row) => <TooltipCell text={row.off_city} /> },
        { name: <span title="Office City">Residence Address</span>, cell: (row) => <TooltipCell text={row.address_s3} /> },

        ...[
            "color_photos3",
            "passport_s3",
            "ic_s3",
            "inc_slips3",
            "bank_states3",
            "aadhaar_s3",
            "pan_s3",
            "verified_s3",
        ].map((field) => ({
            name: <span title={field.replace("_s3", "").replace("_", " ").toUpperCase()}>{field.replace("_s3", "").replace("_", " ").toUpperCase()}</span>,
            cell: (row) => {
                const fileUrl = row[field];
                const fieldName = field.replace("_s3", "").replace("_", " ");

                return fileUrl ? (
                    <FreshDownloadButton
                        fileUrl={fileUrl}
                        fileName={`${fieldName}_${row.id_number || row.id}.pdf`}
                        label="Download"
                    />
                ) : (
                    <span>—</span>
                );
            },
        })),

        { name: <span title="Contact No">Contact No</span>, cell: (row) => <TooltipCell text={row.contact_no} /> },
        { name: <span title="Created At">Created At</span>, cell: (row) => <TooltipCell text={row.created_at} /> },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex gap-1">
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id={`view-tooltip-${row.id}`}>View & Download Application</Tooltip>}
                    >
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleView(row)}
                            style={{ fontSize: "0.7rem" }}
                        >
                            <FiEye />
                        </Button>
                    </OverlayTrigger>
                    {localStorage.getItem("user_role") !== "viewer" && (
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id={`edit-tooltip-${row.id}`}>Edit Application</Tooltip>}
                        >
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleEdit(row)}
                                style={{ fontSize: "0.7rem" }}
                            >
                                <FiEdit />
                            </Button>
                        </OverlayTrigger>
                    )}
                </div>
            ),
            width: "120px",
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            sticky: true,
            right: true,
        },
    ];

    return (
        <div className="card shadow-sm">
            <div className="card-body">
                {!showForm && (
                    <>
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-2">
                            <div className="input-group" style={{ maxWidth: "300px" }}>
                                <span className="input-group-text" style={{ fontSize: "0.8rem" }}>
                                    <FiSearch />
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by name, ID, email, phone..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    style={{ fontSize: "0.8rem" }}
                                />
                                {searchTerm && (
                                    <button
                                        className="btn btn-outline-secondary"
                                        type="button"
                                        onClick={handleClearSearch}
                                        style={{ fontSize: "0.8rem" }}
                                    >
                                        <FiX />
                                    </button>
                                )}
                            </div>
                            <div className="d-flex flex-wrap gap-1">
                                {searchTerm && (
                                    <div className="d-flex align-items-center text-muted" style={{ fontSize: "0.8rem" }}>
                                        Found {filteredApplicants.length} result(s) for "{searchTerm}"
                                    </div>
                                )}
                                {view === "applicant" ? (
                                    <div className=" d-flex justify-content-center align-items-center px-3 py-1 bg-primary bg-opacity-10 text-primary rounded fw-semibold border border-primary gap-2" style={{ fontSize: '13px' }}>
                                        <i className="bi bi-person"></i> Applicant
                                    </div>
                                ) : (
                                    <div className=" d-flex justify-content-center align-items-center px-3 py-1 bg-success bg-opacity-10 text-success rounded fw-semibold border border-success gap-2" style={{ fontSize: '13px' }}>
                                        <i className="bi bi-person-check"></i> Referrer
                                    </div>
                                )}

                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id="switch-tooltip">
                                            Switch to {view === "applicant" ? "referrer" : "applicant"} table
                                        </Tooltip>
                                    }
                                >
                                    <Button
                                        size="sm"
                                        variant="primary"
                                        onClick={() =>
                                            setView(view === "applicant" ? "referrer" : "applicant")
                                        }
                                    >
                                        <FaExchangeAlt />
                                    </Button>
                                </OverlayTrigger>

                                <Button variant="outline-secondary" size="sm" style={{ fontSize: "0.7rem" }} onClick={fetchData} disabled={loading}><FiRefreshCw /></Button>
                                {userRole !== "viewer" && (
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        style={{ fontSize: "0.7rem" }}
                                        onClick={() => setShowForm(true)}
                                    >
                                        + New Application
                                    </Button>
                                )}

                            </div>
                        </div>
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center py-5"><Spinner animation="border" /></div>
                        ) : (
                            <DataTable
                                columns={columns}
                                data={filteredApplicants}
                                pagination
                                highlightOnHover
                                dense
                                responsive
                                persistTableHead
                            />
                        )}
                    </>
                )}
                {showForm && (
                    <Card className="mt-3 border-0 shadow-sm">
                        <Card.Header className="d-flex justify-content-between align-items-center bg-light">
                            <div className="d-flex align-items-center gap-2">
                                <Badge bg={isEditing ? "warning" : "primary"}>
                                    {isEditing ? "Editing" : "Step"} {currentStep + 1} / 6
                                </Badge>
                                <h6 className="mb-0">{steps[currentStep].title}</h6>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    {currentStep === 0 && (
                                        <>
                                            <Button
                                                variant={formType === "Applicant" ? "primary" : "outline-secondary"}
                                                size="sm"
                                                style={{ fontSize: "0.7rem" }}
                                                onClick={() => setFormType("Applicant")}
                                                disabled={isEditing}
                                            >
                                                Applicant
                                            </Button>

                                            <Button
                                                variant={formType === "Guarantor" ? "primary" : "outline-secondary"}
                                                size="sm"
                                                style={{ fontSize: "0.7rem" }}
                                                onClick={() => setFormType("Guarantor")}
                                                disabled={isEditing}
                                            >
                                                Referrer
                                            </Button>
                                        </>
                                    )}

                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        style={{ fontSize: "0.7rem" }}
                                        onClick={handleCancel}
                                    >
                                        <FiX /> Cancel
                                    </Button>
                                </div>

                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Form>
                                <Row>
                                    {currentStep === 0 && (
                                        <>
                                            {formType === "Applicant" && (
                                                <>
                                                    <Col md={6}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label style={{ fontSize: "14px" }}>
                                                                Applicant ID Number (Pass/Fin)
                                                                <span className="text-danger"> *</span>
                                                                {formData._isDuplicate && (
                                                                    <Badge bg="warning" className="ms-2">
                                                                        Duplicate Found
                                                                    </Badge>
                                                                )}
                                                            </Form.Label>
                                                            <div className="position-relative">
                                                                <Form.Control
                                                                    type="text"
                                                                    placeholder="Enter ID to auto-fill details..."
                                                                    value={formData.id_number}
                                                                    onChange={handleIdNumberChange}
                                                                    style={{
                                                                        fontSize: "14px",
                                                                        borderColor: formData._isDuplicate ? '#ffc107' : '',
                                                                        backgroundColor: formData._isDuplicate ? '#fffbf0' : ''
                                                                    }}
                                                                    size="sm"
                                                                    required={true}
                                                                />
                                                                {loading && (
                                                                    <div className="position-absolute" style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}>
                                                                        <Spinner animation="border" size="sm" />
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {!isEditing && (
                                                                <Form.Text className="text-muted" style={{ fontSize: "12px" }}>
                                                                    {formData._isDuplicate
                                                                        ? "This ID is already registered. Consider editing the existing record."
                                                                        : "Start typing to search existing customer records"
                                                                    }
                                                                </Form.Text>
                                                            )}
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={6}>
                                                        <MultipleFileUploadField
                                                            label="Verified screenshot(from MOM/SGWorkpass)"
                                                            value={formData.verified_s3}
                                                            onChange={(file) => handleFileUpload("verified_s3", file)}
                                                            existingFileUrl={existingFiles.verified_s3}
                                                            required={true}
                                                        />
                                                    </Col>
                                                    <Col md={6}>
                                                        <DropdownField
                                                            label="Loan Type"
                                                            options={loanTypeList}
                                                            selected={formData.loan_type}
                                                            onSelect={(val) => setFormData((p) => ({ ...p, loan_type: val }))}
                                                            open={loanTypeOpen}
                                                            setOpen={setLoanTypeOpen}
                                                        />
                                                    </Col>
                                                    <Col md={6}>
                                                        <InputField
                                                            label="Loan Amount"
                                                            type="number"
                                                            value={formData.loan_amt}
                                                            onChange={(e) => handleInputChange(e, "loan_amt")}
                                                        />
                                                    </Col>
                                                    <Col md={6}>
                                                        <InputField
                                                            label="Tenor (Months)"
                                                            type="number"
                                                            value={formData.tenor}
                                                            onChange={(e) => handleInputChange(e, "tenor")}
                                                        />
                                                    </Col>
                                                    <Col md={6}>
                                                        <InputField
                                                            label="Model"
                                                            value={formData.model}
                                                            onChange={(e) => handleInputChange(e, "model")}
                                                        />
                                                    </Col>
                                                    <Col md={6}>
                                                        <InputField
                                                            label="IMEI Number"
                                                            value={formData.imei_no}
                                                            onChange={(e) => handleInputChange(e, "imei_no")}
                                                        />
                                                    </Col>
                                                    <Col md={6}>
                                                        <DropdownField
                                                            label="Loan Purpose"
                                                            options={loanPurposeList}
                                                            selected={formData.loan_purpose}
                                                            onSelect={(val) => setFormData((p) => ({ ...p, loan_purpose: val }))}
                                                            open={loanPurposeOpen}
                                                            setOpen={setLoanPurposeOpen}
                                                        />
                                                    </Col>
                                                    <Col md={6}>
                                                        <DropdownField
                                                            label="Shop Name"
                                                            options={shopNameList}
                                                            selected={formData.shop_name}
                                                            onSelect={(val) => setFormData((p) => ({ ...p, shop_name: val }))}
                                                            open={shopNameOpen}
                                                            setOpen={setShopNameOpen}
                                                        />
                                                    </Col>
                                                </>
                                            )}
                                            {formType === "Guarantor" && (
                                                <Col md={6}>
                                                    <InputField
                                                        label="Applicant ID Number (Pass/Fin)"
                                                        value={formData.id_number}
                                                        onChange={(e) => handleInputChange(e, "id_number")}
                                                    />
                                                </Col>
                                            )}
                                        </>
                                    )}

                                    {currentStep === 1 && (
                                        <>
                                            <Col md={6}>
                                                <InputField label={formType === "Applicant" ? "Name of the Applicant" : "Referrer Name"} value={formData.name} onChange={(e) => handleInputChange(e, "name")} />
                                            </Col>
                                            <Col md={6}>
                                                <MultipleFileUploadField
                                                    label="Photograph together with the product"
                                                    value={formData.photo_s3}
                                                    onChange={(file) => handleFileUpload("photo_s3", file)}
                                                    existingFileUrl={existingFiles.photo_s3}
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <InputField label="Father's / Spouse's Name" value={formData.fs_name} onChange={(e) => handleInputChange(e, "fs_name")} />
                                            </Col>
                                            <Col md={6}>
                                                <InputField label="Nationality" value={formData.nation} onChange={(e) => handleInputChange(e, "nation")} />
                                            </Col>
                                            <Col md={6}>
                                                <DropdownField label="Marital Status" options={["Single", "Married", "Divorced", "Widowed"]} selected={formData.marital} onSelect={(val) => setFormData((p) => ({ ...p, marital: val }))} open={false} setOpen={() => { }} />
                                            </Col>
                                            <Col md={6}>
                                                <InputField label="Date of Birth" type="date" value={formData.dob} onChange={(e) => handleInputChange(e, "dob")} />
                                            </Col>
                                            <Col md={6}>
                                                <DropdownField label="Gender" options={["Male", "Female", "Other"]} selected={formData.gender} onSelect={(val) => setFormData((p) => ({ ...p, gender: val }))} open={false} setOpen={() => { }} />
                                            </Col>
                                            <Col md={6}>
                                                <DropdownField label="Work Permit" options={["S Pass", "E Pass", "WP Pass"]} selected={formData.permit} onSelect={(val) => setFormData((p) => ({ ...p, permit: val }))} open={false} setOpen={() => { }} />
                                            </Col>
                                            <Col md={6}>
                                                <TextareaField label="Residence Address" value={formData.address} onChange={(e) => handleInputChange(e, "address")} />
                                            </Col>
                                            <Col md={6}>
                                                <TextareaField label="Landmark" value={formData.landmark} onChange={(e) => handleInputChange(e, "landmark")} />
                                            </Col>
                                            <Col md={6}>
                                                <InputField label="Mobile Number" value={formData.mob_num} onChange={(e) => handleInputChange(e, "mob_num")} />
                                            </Col>
                                            <Col md={6}>
                                                <InputField label="Email ID" type="email" value={formData.email_id} onChange={(e) => handleInputChange(e, "email_id")} />
                                            </Col>
                                        </>
                                    )}

                                    {currentStep === 2 && (
                                        <>
                                            <Col md={6}>
                                                <DropdownField label="Employment Type" options={empTypeList} selected={formData.emp_type} onSelect={(val) => setFormData((p) => ({ ...p, emp_type: val }))} open={empTypeOpen} setOpen={setEmpTypeOpen} />
                                            </Col>
                                            <Col md={6}>
                                                <InputField label="Professional ID" value={formData.prof_id} onChange={(e) => handleInputChange(e, "prof_id")} />
                                            </Col>
                                            <Col md={6}>
                                                <InputField label="Designation" value={formData.designation} onChange={(e) => handleInputChange(e, "designation")} />
                                            </Col>
                                            <Col md={6}>
                                                <DropdownField label="Business Constitution" options={busConstList} selected={formData.bus_const} onSelect={(val) => setFormData((p) => ({ ...p, bus_const: val }))} open={busConstOpen} setOpen={setBusConstOpen} />
                                            </Col>
                                            <Col md={6}>
                                                <InputField label="Total Experience (Years)" type="number" value={formData.total_exp} onChange={(e) => handleInputChange(e, "total_exp")} />
                                            </Col>
                                            <Col md={6}>
                                                <TextareaField label="Office Address" value={formData.off_address} onChange={(e) => handleInputChange(e, "off_address")} />
                                            </Col>
                                            <Col md={6}>
                                                <TextareaField label="Landmark" value={formData.off_landmark} onChange={(e) => handleInputChange(e, "off_landmark")} />
                                            </Col>
                                            <Col md={6}>
                                                <InputField label="City" value={formData.off_city} onChange={(e) => handleInputChange(e, "off_city")} />
                                            </Col>
                                        </>
                                    )}

                                    {currentStep === 3 && (
                                        <>
                                            <Col md={6}>
                                                <MultipleFileUploadField
                                                    label="Passport size photo"
                                                    value={formData.color_photos3}
                                                    onChange={(file) => handleFileUpload("color_photos3", file)}
                                                    existingFileUrl={existingFiles.color_photos3}
                                                />
                                            </Col>

                                            <Col md={6}>
                                                <MultipleFileUploadField
                                                    label="ID Proof (Passport) (Front & Back)"
                                                    value={formData.passport_s3}
                                                    onChange={(file) => handleFileUpload("passport_s3", file)}
                                                    existingFileUrl={existingFiles.passport_s3}
                                                    required={true}
                                                />
                                            </Col>

                                            <Col md={6}>
                                                <MultipleFileUploadField
                                                    label="IC Copy (Front & Back)"
                                                    value={formData.ic_s3}
                                                    onChange={(file) => handleFileUpload("ic_s3", file)}
                                                    existingFileUrl={existingFiles.ic_s3}
                                                    required={true}
                                                />
                                            </Col>

                                            <Col md={6}>
                                                <MultipleFileUploadField
                                                    label="Income Verification (Salary Slips – Last 4 Months)"
                                                    value={formData.inc_slips3}
                                                    onChange={(file) => handleFileUpload("inc_slips3", file)}
                                                    existingFileUrl={existingFiles.inc_slips3}
                                                />
                                            </Col>

                                            <Col md={6}>
                                                <MultipleFileUploadField
                                                    label="Bank Statements (Last 4 Months)"
                                                    value={formData.bank_states3}
                                                    onChange={(file) => handleFileUpload("bank_states3", file)}
                                                    existingFileUrl={existingFiles.bank_states3}
                                                />
                                            </Col>
                                        </>
                                    )}

                                    {currentStep === 4 && (
                                        <>
                                            <Col md={6}>
                                                <MultipleFileUploadField
                                                    label="Aadhaar Proof"
                                                    value={formData.aadhaar_s3}
                                                    onChange={(file) => handleFileUpload("aadhaar_s3", file)}
                                                    existingFileUrl={existingFiles.aadhaar_s3}
                                                />
                                            </Col>

                                            <Col md={6}>
                                                <MultipleFileUploadField
                                                    label="PAN Card"
                                                    value={formData.pan_s3}
                                                    onChange={(file) => handleFileUpload("pan_s3", file)}
                                                    existingFileUrl={existingFiles.pan_s3}
                                                />
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group className="mb-4">
                                                    <Form.Label>Residence Address</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        placeholder="Enter your complete residence address"
                                                        value={formData.address_s3}
                                                        onChange={(e) =>
                                                            setFormData({ ...formData, address_s3: e.target.value })
                                                        }
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group className="mb-4">
                                                    <Form.Label>Contact Number (Native)</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Enter native contact number"
                                                        value={formData.contact_no}
                                                        onChange={(e) =>
                                                            setFormData({ ...formData, contact_no: e.target.value })
                                                        }
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </>
                                    )}

                                    {currentStep === 5 && (
                                        <Col md={12}>
                                            <h5 className="mb-3">Review Your Application</h5>

                                            <Accordion defaultActiveKey="0" flush>
                                                {[
                                                    {
                                                        title: "Loan Details",
                                                        fields:
                                                            formType === "Applicant"
                                                                ? [
                                                                    ["ID Number", formData.id_number],
                                                                    ["verified screenshot(from MOM/SGWorkpass)", formData.verified_s3 || existingFiles.verified_s3 ?
                                                                        <a href={formData.verified_s3 || existingFiles.verified_s3} target="_blank" rel="noreferrer">View File</a> : "—"],
                                                                    ["Loan Type", formData.loan_type],
                                                                    ["Loan Amount", formData.loan_amt],
                                                                    ["Tenor (Months)", formData.tenor],
                                                                    ["Property Model", formData.model],
                                                                    ["IMEI Number", formData.imei_no],
                                                                    ["Loan Purpose", formData.loan_purpose],
                                                                    ["Shop Name", formData.shop_name],
                                                                ]
                                                                : [["ID Number", formData.id_number]],
                                                    },
                                                    {
                                                        title: formType === "Applicant" ? "Applicant Details" : "Referrer Details",
                                                        fields: [
                                                            ["Name", formData.name],
                                                            ["Father/Spouse", formData.fs_name],
                                                            ["Nationality", formData.nation],
                                                            ["Marital Status", formData.marital],
                                                            ["DOB", formData.dob],
                                                            ["Gender", formData.gender],
                                                            ["Work Permit", formData.permit],
                                                            ["Mobile Number", formData.mob_num],
                                                            ["Email ID", formData.email_id],
                                                            ["Address", formData.address],
                                                            ["Landmark", formData.landmark],
                                                            ["Photo", formData.photo_s3 || existingFiles.photo_s3 ?
                                                                <a href={formData.photo_s3 || existingFiles.photo_s3} target="_blank" rel="noreferrer">View File</a> : "—"],
                                                        ],
                                                    },
                                                    {
                                                        title: "Employment Details",
                                                        fields: [
                                                            ["Employment Type", formData.emp_type],
                                                            ["Professional ID", formData.prof_id],
                                                            ["Designation", formData.designation],
                                                            ["Business Constitution", formData.bus_const],
                                                            ["Total Experience", formData.total_exp ? `${formData.total_exp} years` : "—"],
                                                            ["Office Address", formData.off_address],
                                                            ["Office Landmark", formData.off_landmark],
                                                            ["City", formData.off_city],
                                                        ],
                                                    },
                                                    {
                                                        title: "Documents",
                                                        fields: [
                                                            ["Colour Photograph", formData.color_photos3 || existingFiles.color_photos3],
                                                            ["Passport", formData.passport_s3 || existingFiles.passport_s3],
                                                            ["IC Copy", formData.ic_s3 || existingFiles.ic_s3],
                                                            ["Salary Slips", formData.inc_slips3 || existingFiles.inc_slips3],
                                                            ["Bank Statements", formData.bank_states3 || existingFiles.bank_states3],
                                                        ].map(([label, value]) => [
                                                            label,
                                                            value ? <a href={value} target="_blank" rel="noreferrer">View File</a> : "—"
                                                        ]),
                                                    },
                                                    {
                                                        title: "Other Details",
                                                        fields: [
                                                            ["Aadhaar Proof", formData.aadhaar_s3 || existingFiles.aadhaar_s3],
                                                            ["PAN Card", formData.pan_s3 || existingFiles.pan_s3],
                                                            ["Residence Address", formData.address_s3],
                                                            ["Contact Number", formData.contact_no],
                                                        ].map(([label, value]) => [
                                                            label,
                                                            (label.includes("Aadhaar") || label.includes("PAN")) && value ?
                                                                <a href={value} target="_blank" rel="noreferrer">View File</a> : value || "—"
                                                        ]),
                                                    },
                                                ].map((section, idx) => (
                                                    <Accordion.Item
                                                        eventKey={idx.toString()}
                                                        key={section.title}
                                                        style={{ border: "1px solid #ddd", borderRadius: "6px", marginBottom: "12px", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}
                                                    >
                                                        <Accordion.Header style={{ outline: "none" }}>{section.title}</Accordion.Header>
                                                        <Accordion.Body>
                                                            {section.fields.map(([label, value]) => (
                                                                <div
                                                                    className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2"
                                                                    key={label}
                                                                    style={{ gap: "24px" }}
                                                                >
                                                                    <span><strong>{label}:</strong></span>
                                                                    <span>{value || "—"}</span>
                                                                </div>
                                                            ))}
                                                        </Accordion.Body>
                                                    </Accordion.Item>
                                                ))}
                                            </Accordion>
                                        </Col>
                                    )}
                                </Row>
                            </Form>
                            <div className="d-flex justify-content-between mt-4">
                                <Button variant="secondary" onClick={handlePreviousStep} disabled={currentStep === 0} size="sm">
                                    Previous
                                </Button>
                                <Button
                                    variant={isEditing ? "warning" : "primary"}
                                    onClick={handleNextStep}
                                    size="sm"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            {isEditing ? "Updating..." : "Submitting..."}
                                        </>
                                    ) : currentStep === 5 ? (
                                        isEditing ? "Update Application" : "Submit Application"
                                    ) : (
                                        "Next"
                                    )}
                                </Button>

                            </div>

                            <ProgressBar currentStep={currentStep} steps={steps} />
                        </Card.Body>
                    </Card>
                )}
            </div>
            {
                viewModalShow && (
                    <ApplicationViewModal
                        show={viewModalShow}
                        onHide={() => setViewModalShow(false)}
                        application={selectedApplication}
                        type={view}
                    />
                )
            }
            <ToastContainer position="top-right" autoClose={1500} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
        </div>
    );
};

export default Application;